import { useState } from 'react'
import {
  Box, Button, Collapse, Divider, Drawer, IconButton, List, ListItemButton, ListItemText, Stack,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import { ChevronDown, X } from 'lucide-react'
import { colors, zIndex } from '@/theme'
import type { NavCategory, NavIndustry } from '@/content/types'
import { resourceLinks } from '@/content/site'
import { Logo } from '@/components/ui/Logo'

interface MobileDrawerProps {
  open: boolean
  onClose: () => void
  categories: NavCategory[]
  industries: NavIndustry[]
}

export function MobileDrawer({ open, onClose, categories, industries }: MobileDrawerProps) {
  const [section, setSection] = useState<'products' | 'industries' | null>('products')
  const toggle = (s: 'products' | 'industries') => () => setSection((cur) => (cur === s ? null : s))

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      sx={{ zIndex: zIndex.drawer }}
      slotProps={{ paper: { sx: { width: { xs: '84vw', sm: 380 }, bgcolor: colors.bg, backgroundImage: 'none' } } }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 3 }}>
        <Logo height={46} />
        <IconButton aria-label="Close menu" onClick={onClose} sx={{ color: colors.white }}>
          <X size={24} />
        </IconButton>
      </Stack>
      <Divider />

      <Box sx={{ overflowY: 'auto', flex: 1 }}>
        <List disablePadding>
          <ListItemButton component={RouterLink} to="/" onClick={onClose}>
            <ListItemText primaryTypographyProps={{ variant: 'h6' }} primary="Home" />
          </ListItemButton>
          <Divider />
          <ListItemButton onClick={toggle('products')}>
            <ListItemText primaryTypographyProps={{ variant: 'h6' }} primary="Products" />
            <ChevronDown size={18} style={{ transform: section === 'products' ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />
          </ListItemButton>
          <Collapse in={section === 'products'} unmountOnExit>
            <ListItemButton component={RouterLink} to="/products" onClick={onClose} sx={{ pl: 4 }}>
              <ListItemText primary="All Products" />
            </ListItemButton>
            {categories.map((cat) => (
              <ListItemButton key={cat.slug} component={RouterLink} to={`/products/${cat.slug}`} onClick={onClose} sx={{ pl: 4 }}>
                <ListItemText primary={cat.name} secondary={cat.comingSoon ? 'Coming Soon' : undefined} />
              </ListItemButton>
            ))}
          </Collapse>

          <Divider />

          <ListItemButton onClick={toggle('industries')}>
            <ListItemText primaryTypographyProps={{ variant: 'h6' }} primary="Industries" />
            <ChevronDown size={18} style={{ transform: section === 'industries' ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }} />
          </ListItemButton>
          <Collapse in={section === 'industries'} unmountOnExit>
            <ListItemButton component={RouterLink} to="/industries" onClick={onClose} sx={{ pl: 4 }}>
              <ListItemText primary="All Industries" />
            </ListItemButton>
            {industries.map((ind) => (
              <ListItemButton key={ind.slug} component={RouterLink} to={`/industries/${ind.slug}`} onClick={onClose} sx={{ pl: 4 }}>
                <ListItemText primary={ind.name} />
              </ListItemButton>
            ))}
          </Collapse>

          <Divider />
          <ListItemButton component={RouterLink} to="/about" onClick={onClose}>
            <ListItemText primaryTypographyProps={{ variant: 'h6' }} primary="About" />
          </ListItemButton>
          <ListItemButton component={RouterLink} to="/contact" onClick={onClose}>
            <ListItemText primaryTypographyProps={{ variant: 'h6' }} primary="Contact" />
          </ListItemButton>

          <Divider />
          {resourceLinks.map((l) => (
            <ListItemButton key={l.to} component={RouterLink} to={l.to} onClick={onClose}>
              <ListItemText primaryTypographyProps={{ variant: 'h6' }} primary={l.label} />
            </ListItemButton>
          ))}
        </List>
      </Box>

      <Box sx={{ p: 3 }}>
        <Button fullWidth variant="contained" component={RouterLink} to="/contact" onClick={onClose}>
          Request Quote
        </Button>
      </Box>
    </Drawer>
  )
}
